import { DEFAULT_DROPDOWN_VALUE } from "../../../common/constants/ApiConstants";

/**
 * Picks the initial value for a data-entry field that the transaction may not have persisted yet:
 * the persisted value when there is one, otherwise whatever is live in the form (AB#35149).
 *
 * `DATA_ENTRY_FIELDS_FORM` is owned by DataEntryDetailsRightPane's reduxForm HOC with
 * `enableReinitialize: true`, and its initialValues spread the CURRENT form values and then
 * override specific keys straight from `transactionDetails`:
 *
 *     initialValues: { ...formValues, [CASE_NUMBER]: transactionDetails.caseNumber, ... }
 *
 * Because initial ends up equal to current for everything else, almost no field is ever dirty, so
 * `keepDirtyOnReinitialize: true` protects nothing - and the overridden keys are then forced to the
 * transaction's value. For anything Quick Entry seeded or the specialist typed but has not
 * committed, that value is null (writeFieldToForm writes redux-form only; it never mirrors into
 * transactionDetails, and mirrorData has no case for Case Number or Clinic at all). So opening the
 * requestor modal, or a Search refreshing any slice, re-initialised those fields to empty.
 *
 * The persisted value stays authoritative, so a specialist edit still shows as dirty against it and
 * Classic Mode is unaffected wherever a value IS persisted. Only the "nothing persisted" case
 * changes, and there keeping the live draft is correct in both panes.
 *
 * Never logs a value - these fields are PHI-adjacent (.claude/rules/hipaa-compliance.md).
 */
// Two type parameters on purpose: the same field can be a number on the transaction and a string
// in the form (a clinic id persists as number, rehydrates/writes as string), so forcing one type
// would reject exactly the case this exists for.
export function persistedOrDraft<TPersisted, TDraft>(
    persisted: TPersisted | null | undefined,
    draft: TDraft | null | undefined,
): TPersisted | TDraft | null {
    if (hasValue(persisted)) {
        return persisted as TPersisted;
    }
    return hasValue(draft) ? (draft as TDraft) : null;
}

/**
 * "" is not a deliberate blank here - it is a field the transaction has never carried. 0 and the
 * "- Select -" sentinel are dropdown placeholders, not selections (AB#35070). Dates and other
 * objects count as values whenever present.
 */
function hasValue(value: unknown): boolean {
    if (value === null || value === undefined || value === "") {
        return false;
    }
    if (typeof value === "number") {
        return value > 0;
    }
    if (typeof value === "string") {
        return value !== DEFAULT_DROPDOWN_VALUE && value !== "0";
    }
    return true;
}

export default persistedOrDraft;
